<script setup lang="ts">
/**
 * Check: the reference photo with the fitted front elevation drawn over it (ui-spec §2.2): the
 * working design's boards through the family generator, mapped onto the piece's front face on the
 * photo (through the fit's perspective quad when it has one, FND-16); top, bottom and bay-1
 * shelves numbered (hovered or focused in --sel). The photo is cropped to the piece so it is large
 * enough to check against (FND-17), the tags sit in a gutter beside it so they never clip
 * (FND-22), and a click opens it whole in the lightbox.
 */
import { computed, ref } from 'vue';
import Icon from './Icon.vue';
import { ui } from '@/app/state';
import { lineLabel } from '@/app/format';
import { cropFor, fitOverlay } from '@/app/overlay';

const img = computed(() => ui.ref.images[0] ?? null);
const ov = computed(() => fitOverlay.value);
const show = computed(() => !!img.value && !!ov.value);
const dims = ref<{ w: number; h: number } | null>(null);
const natural = computed(() => {
  const w = img.value?.width ?? dims.value?.w;
  const h = img.value?.height ?? dims.value?.h;
  return w && h ? { w, h } : null;
});
/** The shown part of the photo (normalised): the piece and a margin, or the whole photo. */
const crop = computed(() => (show.value && natural.value ? cropFor(ov.value, natural.value.w / natural.value.h) : { x: 0, y: 0, w: 1, h: 1 }));
const cropped = computed(() => crop.value.w < 0.98 || crop.value.h < 0.98);
const aspect = computed(() => (natural.value ? (natural.value.w * crop.value.w) / (natural.value.h * crop.value.h) : 0.75).toFixed(4));
const imgStyle = computed(() => {
  const c = crop.value;
  return { width: `${(100 / c.w).toFixed(4)}%`, height: `${(100 / c.h).toFixed(4)}%`, left: `${(-c.x / c.w * 100).toFixed(4)}%`, top: `${(-c.y / c.h * 100).toFixed(4)}%` };
});
const viewBox = computed(() => `${crop.value.x} ${crop.value.y} ${crop.value.w} ${crop.value.h}`);
/** Numbered tags: one per horizontal the cards refer to, at its left end on the photo (shown part). */
const tags = computed(() => {
  const o = ov.value;
  if (!o) return [];
  const c = crop.value;
  const rows = o.lines.length ? o.lines.map((l) => ({ id: l.partId, y: l.y })) : o.rects.filter((r) => r.tag).map((r) => ({ id: r.id, y: r.tagY }));
  return rows.map((t) => ({ id: t.id, top: Math.min(1, Math.max(0, (t.y - c.y) / c.h)) })).sort((a, b) => a.top - b.top);
});
const lit = computed(() => new Set(ui.litLines));
const caption = computed(() => {
  if (!show.value) return ui.analysis.status === 'manual' ? 'Photo 1 · not used for a manual setup' : 'Your photo';
  const how = ov.value?.perspective ? 'the fitted elevation drawn in the photo’s perspective' : 'the fitted elevation drawn over it';
  return `Photo 1${cropped.value ? ', cropped to the piece' : ''} · ${how}`;
});
function onLoad(e: Event) {
  const el = e.target as HTMLImageElement;
  if (el.naturalWidth) dims.value = { w: el.naturalWidth, h: el.naturalHeight };
}
function light(id: string | null) { ui.litLines = id ? [id] : []; }
function toggle(id: string) { light(lit.value.has(id) && ui.litLines.length === 1 ? null : id); }
function enlarge() { ui.lightbox = { kind: 'ref' }; }
</script>

<template>
  <template v-if="img">
    <div class="ref-frame" :class="{ 'has-tags': show && tags.length }" :style="{ '--ar': aspect }" @pointerleave="light(null)">
      <div class="ref-photo">
        <img :src="img.src" :alt="img.alt" :style="imgStyle" @load="onLoad">
        <svg v-if="show && ov" :viewBox="viewBox" preserveAspectRatio="none" aria-hidden="true">
          <polygon class="ref-outline" :points="ov.quad.map((p) => `${p.x},${p.y}`).join(' ')" vector-effect="non-scaling-stroke" />
          <polygon v-for="r in ov.rects" :key="`h-${r.id}`" class="ref-halo" :points="r.points" vector-effect="non-scaling-stroke" />
          <polygon
            v-for="r in ov.rects"
            :key="r.id"
            class="ref-board"
            :class="{ 'is-lit': lit.has(r.id) }"
            :data-board="r.id"
            :points="r.points"
            vector-effect="non-scaling-stroke"
          />
          <line v-for="l in ov.lines" :key="l.partId" class="ref-line" :class="{ 'is-lit': lit.has(l.partId) }" :data-line="l.partId" :x1="ov.carcass.left - 0.02" :x2="ov.carcass.right + 0.02" :y1="l.y" :y2="l.y" vector-effect="non-scaling-stroke" />
        </svg>
        <div class="ref-open" data-act="enlarge-ref" aria-hidden="true" title="Enlarge the photo" @click="enlarge" />
        <button type="button" class="ref-zoom" data-act="zoom-ref" aria-label="Enlarge the reference photo" @click="enlarge"><Icon name="expand" /><span>Enlarge</span></button>
        <p v-if="show && ov && !ov.perspective" class="ref-straight" data-note="straight-on">Overlay drawn straight-on</p>
      </div>
      <div v-if="show" class="ref-tags">
        <button
          v-for="t in tags"
          :key="t.id"
          type="button"
          class="ref-tag"
          :class="{ 'is-lit': lit.has(t.id) }"
          :data-line="t.id"
          :style="{ top: `clamp(14px, ${(t.top * 100).toFixed(2)}%, calc(100% - 14px))` }"
          :aria-label="`${lineLabel(t.id)}: highlight it on the photo and the model`"
          @pointerover="light(t.id)"
          @focus="light(t.id)"
          @click="toggle(t.id)"
        >
          {{ lineLabel(t.id) }}
        </button>
      </div>
    </div>
    <figcaption>{{ caption }}</figcaption>
  </template>
  <figcaption v-else class="ref-none">No reference photo: these values are a starting point set up by hand.</figcaption>
</template>
